import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { TriangleAlertIcon } from "lucide-react";

import { Divider } from "simplydo/react/ui/divider";
import { Icon } from "simplydo/react/ui/icon";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type { ChallengeDetail, ChallengeProblem } from "../types";

type ChallengeProblemsProps = { challenge: ChallengeDetail };

const ChallengeProblems = ({ challenge }: ChallengeProblemsProps) => {
  const [challengeProblemInfo, setChallengeProblemInfo] =
    useState<ChallengeProblem | null>(null);
  const user = useAppSelector((state) => state.user);
  const challengeId = challenge._id;

  const canManageChallenge = useMemo(
    () =>
      util.canManageChallenge(user, challenge) ||
      util.hasPermission(user, "challenge.editSettings", challenge._id),
    [user, challenge],
  );

  const { t } = useTranslation();

  const getProblems = useCallback(() => {
    if (challengeId && canManageChallenge) {
      api.challenges.getProblems(
        challengeId,
        (data) => {
          setChallengeProblemInfo(data);
        },
        () => {},
      );
    }
  }, [canManageChallenge, challengeId]);

  useEffect(() => {
    getProblems();
  }, [getProblems]);

  if (
    challengeProblemInfo?.usersNotInAudience ||
    challengeProblemInfo?.ipManagementMissingStatement
  ) {
    const usersNotInAudience = challengeProblemInfo.usersNotInAudience;
    return (
      <Message color="red">
        <Icon icon={TriangleAlertIcon} />
        <MessageHeader>Configuration problems</MessageHeader>
        <MessageContent>
          {challengeProblemInfo?.ipManagementMissingStatement && (
            <>
              <MessageHeader style={{ marginBottom: 10 }}>
                IP management misconfiguration
              </MessageHeader>
              <MessageContent>
                This {t("generic.challenge")} has IP management enabled but does
                not have an IP statement configured. This means that users will
                be unable to create ideas for this {t("generic.challenge")}.
                Please disable this setting or configure an IP statement.
                <Divider hidden />
              </MessageContent>
            </>
          )}
          {usersNotInAudience && (
            <>
              <MessageHeader style={{ marginBottom: 10 }}>
                Audience misconfiguration:{" "}
                {util.pluralise(
                  (usersNotInAudience.assessors?.length ?? 0) +
                    (usersNotInAudience.assignees?.length ?? 0),
                  "user",
                  "users",
                )}{" "}
                cannot currently view this {t("generic.challenge")}
              </MessageHeader>
              <MessageContent>
                This {t("generic.challenge")} has assignees or assessors that
                aren&apos;t currently in the audience.{" "}
                {t("common:capitalise", { key: "generic.idea" })} assignees or
                assessors not in the {t("generic.challenge")}
                &apos;s audience will not be able to view the{" "}
                {t("generic.challenge")} and may not be able to find their
                assigned ideas.
              </MessageContent>
              {(usersNotInAudience.assessors?.length ?? 0) > 0 ? (
                <>
                  <MessageContent style={{ marginTop: 10 }}>
                    The following assessors are not currently in the audience:{" "}
                    <b>
                      {(usersNotInAudience.assessorUsers ?? [])
                        .map((assessor) => assessor.profile.fullName)
                        .join(", ")}
                    </b>
                  </MessageContent>
                  <Divider hidden />
                </>
              ) : null}
              {(usersNotInAudience.assignees?.length ?? 0) > 0 ? (
                <>
                  <MessageContent style={{ marginTop: 10 }}>
                    The following project assignees are not currently in the
                    audience:{" "}
                    <b>
                      {(usersNotInAudience.assigneeUsers ?? [])
                        .map((assignee) => assignee.profile.fullName)
                        .join(", ")}
                    </b>
                  </MessageContent>
                  <Divider hidden />
                </>
              ) : null}
            </>
          )}
          <Link to={`/challenges/${challengeId}/settings`}>
            Click here to configure the {t("generic.challenge")} settings
          </Link>
        </MessageContent>
      </Message>
    );
  }
  return null;
};

export default ChallengeProblems;
